// Globální store pro top-bar navigační indikátor.
//
// Řeší „mrtvé okno" KLIK → MOUNT u lazy route chunků: React Router v7 balí změnu
// locationu do startTransition, takže Suspense fallback (<CSpinner/> kolem <Outlet/>
// v DefaultLayout) se při přechodu mezi už-namountovanými routami NEUKÁŽE — stará
// stránka zůstává na místě, dokud se nový chunk nenačte a nenamountuje. Tenhle
// indikátor tu díru vizuálně kryje.
//
// Detekce je univerzální: patchujeme history.pushState/replaceState, takže chytneme
// <Link>/CNavItem klik, programové navigate() i <Navigate> redirect (vše teče přes
// History API). START = změna pathname v history; END = commit nové location
// (NavigationProgress volá commit() z useEffect na useLocation — ten se aktualizuje
// až po dokončení transition, tj. po namountování nové stránky). Datový fetch uvnitř
// stránky řeší vlastní DataLoadProgress, sem nepatří.

interface State { active: boolean; progress: number }

let state: State = { active: false, progress: 0 };
const listeners = new Set<() => void>();

// Poslední commitnutá pathname — proti ní se poměřuje, jestli navigace mění routu
// (a má tedy naběhnout bar), nebo jde jen o in-page replaceState (query apod.).
let committedPath = typeof window !== 'undefined' ? window.location.pathname : '/';

let trickleTimer: ReturnType<typeof setInterval> | undefined;
let finishTimer: ReturnType<typeof setTimeout> | undefined;
let safetyTimer: ReturnType<typeof setTimeout> | undefined;
let startDelayTimer: ReturnType<typeof setTimeout> | undefined;

const TRICKLE_MS = 240;
// Jak dlouho po dokončení necháme bar (na 100 %) vyfadeovat, než ho odmountujeme.
// Musí být >= délce opacity přechodu v NavigationProgress (wrapperStyle), ať se
// stihne doblednout, než zmizí z DOM.
const FINISH_FADE_MS = 320;
// Pojistka: bar vždy dojede, i kdyby commit nikdy nepřišel (chyba / stejná routa).
const SAFETY_TIMEOUT_MS = 10_000;
// Delay before the bar appears. If the new route mounts within this window the
// bar never shows at all, so fast navigations (e.g. /login -> /register, light
// pages) stay flicker-free; the bar only surfaces on genuinely slow transitions
// (heavy lazy chunks like Charts).
const START_DELAY_MS = 150;

const emit = (): void => { listeners.forEach((l) => l()); };
const setState = (next: State): void => { state = next; emit(); };

const stopTrickle = (): void => {
  if (trickleTimer !== undefined) { clearInterval(trickleTimer); trickleTimer = undefined; }
};

// Actually shows the bar and starts trickling. Called only after START_DELAY_MS
// elapses (or immediately when a bar is already running).
const beginBar = (): void => {
  startDelayTimer = undefined;
  if (finishTimer !== undefined) { clearTimeout(finishTimer); finishTimer = undefined; }
  // Když už bar běží a nedojel k 0.9, necháme naběhlý progres; jinak start od nuly.
  const base = state.active && state.progress < 0.9 ? state.progress : 0.08;
  setState({ active: true, progress: base });

  stopTrickle();
  trickleTimer = setInterval(() => {
    // Asymptoticky k 0.9 — bar se plynule blíží, ale „nedojede", dokud nepřijde done().
    const p = state.progress + (0.9 - state.progress) * 0.12;
    setState({ active: true, progress: Math.min(p, 0.9) });
  }, TRICKLE_MS);

  if (safetyTimer !== undefined) clearTimeout(safetyTimer);
  safetyTimer = setTimeout(done, SAFETY_TIMEOUT_MS);
};

// Public entry, wired to the History-API events. Debounces the bar behind
// START_DELAY_MS so fast navigations never flash it.
const start = (): void => {
  // Already running (a slow transition is still in progress) -> keep it going.
  if (state.active) { beginBar(); return; }
  if (startDelayTimer !== undefined) clearTimeout(startDelayTimer);
  startDelayTimer = setTimeout(beginBar, START_DELAY_MS);
};

const done = (): void => {
  // Cancel a pending delayed start: if the transition finished before the bar
  // was shown, don't show it at all.
  if (startDelayTimer !== undefined) { clearTimeout(startDelayTimer); startDelayTimer = undefined; }
  if (!state.active) return;
  stopTrickle();
  if (safetyTimer !== undefined) { clearTimeout(safetyTimer); safetyTimer = undefined; }
  setState({ active: true, progress: 1 });
  if (finishTimer !== undefined) clearTimeout(finishTimer);
  finishTimer = setTimeout(() => {
    finishTimer = undefined;
    setState({ active: false, progress: 0 });
  }, FINISH_FADE_MS);
};

// Arrow-fn properties (ne metody) — bezpečné destrukturovat / předat jako callback
// do useSyncExternalStore bez ztráty `this`.
export const navigationProgress = {
  subscribe: (cb: () => void): (() => void) => {
    listeners.add(cb);
    return (): void => { listeners.delete(cb); };
  },
  getSnapshot: (): State => state,
  /**
   * Zaznamenej, že se nová location namountovala (commit transition). Aktualizuje
   * referenční pathname a dokončí případný běžící bar. Volá NavigationProgress
   * z useEffect na useLocation().pathname.
   */
  commit: (pathname: string): void => {
    committedPath = pathname;
    done();
  },
};

// --- Univerzální detekce startu navigace přes History API -------------------

const LOCATION_EVENT = 'statotest:locationchange';

const handleLocationChange = (): void => {
  // pushState/replaceState už proběhl → window.location je aktuální.
  if (window.location.pathname !== committedPath) start();
};

const installOnce = (): void => {
  const w = window as unknown as { __statotestNavProgressInstalled?: boolean };
  if (w.__statotestNavProgressInstalled) return;
  w.__statotestNavProgressInstalled = true;

  const patch = (key: 'pushState' | 'replaceState'): void => {
    const orig = history[key].bind(history);
    history[key] = function (...args: Parameters<History['pushState']>): void {
      orig(...args);
      window.dispatchEvent(new Event(LOCATION_EVENT));
    };
  };
  patch('pushState');
  patch('replaceState');
  window.addEventListener(LOCATION_EVENT, handleLocationChange);
  window.addEventListener('popstate', handleLocationChange);
};

if (typeof window !== 'undefined') installOnce();
